import React, { FC } from 'react';
import { clsx } from 'clsx';

interface PaginationProps {
    /** 현재 페이지 (0-based) */
    currentPage: number;
    /** 전체 페이지 수 */
    totalPages: number;
    /** 전체 아이템(게시물) 개수 (선택사항) */
    totalElements?: number;
    /** 페이지 버튼 묶음 개수 
     * @example 5 => 한 화면에 최대 5개 페이지 버튼 노출
     * 0 => 버튼이 아닌 "현재 / 전체" 형태로 표시
     */
    pageBtnCount?: number;
    /** 페이지 변경 시 호출되는 콜백 */
    onChange: (page: number) => void;
    type?: string
    /**  페이지 네이션 타입 */
}

/**
 * 재사용이 가능한 Pagination 컴포넌트.
 * - 내부에서 어떤 데이터를 fetch하지 않음
 * - currentPage/totalPages/pageBtnCount/onChange 등 모두 props로 제어
 */
export const Pagination: FC<PaginationProps> = ({
    currentPage,
    totalPages,
    totalElements = 0,
    pageBtnCount = 0,
    onChange,
    type = 'type2',
}) => {
    // 페이지 이동
    const goToPage = (target: number) => {
        if (target < 0) return onChange(0);
        if (target >= totalPages) return onChange(totalPages - 1);
        onChange(target);
    };

    // 페이지 버튼을 몇 개 보여줄지 여부
    const isBtnType = pageBtnCount > 0;

    // 실제 페이지 버튼들을 그리는 함수
    const renderPageNumbers = () => {
        // 1) pageBtnCount=0 이면 "현재 / 전체" 식으로만 표시
        if (!isBtnType) {
            return (
                <div className="pageNum">
                    <strong>{currentPage + 1}</strong>/<span>{totalPages > 0 ? totalPages : 1}</span>
                </div>
            );
        }

        // 2) pageBtnCount>0 이면 특정 갯수만큼 페이지 버튼 노출 (예: 5개씩)
        const groupStart = Math.floor(currentPage / pageBtnCount) * pageBtnCount;
        const pages = Array.from({ length: pageBtnCount }, (_, i) => groupStart + i).filter(
            (p) => p < totalPages,
        );

        return (
            <div className="pageNum">
                {pages.length > 0
                    ? pages.map((p) => (
                        <button
                            key={p}
                            title={p === currentPage ? '현재 페이지' : ''}
                            className={p === currentPage ? 'current' : ''}
                            onClick={() => goToPage(p)}
                        >
                            {p + 1}
                        </button>
                    ))
                    : (
                        // totalElements가 0, totalPages도 0인 경우를 대비한 예시
                        <button className="current">1</button>
                    )
                }
            </div>
        );
    };

    // "이전/다음" 이동 시 몇 칸씩 이동할지 (기본 1)
    // 만약 "5페이지씩 건너뛰기" 등을 원한다면 수정 가능
    const moveCnt = 1;

    // 마지막 페이지로 이동
    const moveToLastPage = () => {
        const lastPage = totalPages - 1;
        if (currentPage !== lastPage) {
            goToPage(lastPage);
        }
    };

    // clsx로 동적 클래스 설정
    const paginationClass = clsx('pagination', type);

    return (
        <div className={paginationClass}>
            {/* 첫 페이지 이동 */}
            <button
                className="btn-page left-last"
                onClick={() => goToPage(0)}
                disabled={currentPage <= 0}
            >
                <span className="sr-only">첫 페이지</span>
            </button>

            {/* 이전 페이지 이동 */}
            <button
                className="btn-page left"
                onClick={() => goToPage(currentPage - moveCnt)}
                disabled={currentPage <= 0}
            >
                <span className="sr-only">이전 페이지</span>
            </button>

            {/* 페이지 번호(혹은 x / y) */}
            {renderPageNumbers()}

            {/* 다음 페이지 이동 */}
            <button
                className="btn-page right"
                onClick={() => goToPage(currentPage + moveCnt)}
                disabled={currentPage >= totalPages - 1}
            >
                <span className="sr-only">다음 페이지</span>
            </button>

            {/* 마지막 페이지 이동 */}
            <button
                className="btn-page right-last"
                onClick={moveToLastPage}
                disabled={currentPage >= totalPages - 1}
            >
                <span className="sr-only">마지막 페이지</span>
            </button>
        </div>
    );
};


